/**
 * `StatusPage` — GVW-157 system status link.
 *
 * Per brief §2 / §8: status chip with `aria-label` "System status: {state};
 * click for status page" and an `<a target="_blank" rel="noopener noreferrer">`
 * link to the external status page. Phase 2 = static placeholder per dispatch
 * §1; Phase 3 will poll `@geosoft/platform-sdk` health endpoint.
 *
 * Defence variant uses local mirror only per brief §13: external link is
 * hidden when `defenceVariant.active`.
 *
 * TODO(WP-E13): wire to `sdk.system.health()` polling and surface real
 * incident/degraded states.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-157/brief.md
 */

import { useCallback, useEffect, useState } from 'react';
import { tokens } from '@/design';
import { useDefenceVariant } from '@/design/hooks';
import { useLiveRegion } from '@/state/liveRegion';

type StatusState = 'healthy' | 'degraded' | 'incident' | 'offline';

interface SystemStatus {
  state: StatusState;
  updatedAt: string;
  statusUrl?: string;
  message: string;
}

const STATUS_GLYPH: Record<StatusState, { icon: string; label: string; color: string }> = {
  healthy: { icon: 'monitor_heart', label: 'All systems operational', color: tokens.color.semantic.success },
  degraded: { icon: 'warning', label: 'Degraded performance', color: tokens.color.semantic.warning },
  incident: { icon: 'error', label: 'Service incident', color: tokens.color.semantic.danger },
  offline: { icon: 'sync_problem', label: 'Status unavailable', color: tokens.color.ink.muted },
};

export function StatusPage() {
  const liveRegion = useLiveRegion();
  const defenceVariant = useDefenceVariant();
  const [status, setStatus] = useState<SystemStatus | null>(null);

  const refresh = useCallback(() => {
    // Phase 2 placeholder: in defence variant we never reach out; otherwise
    // we still return a static healthy result until WP-E13 wires the SDK.
    if (defenceVariant.active) {
      setStatus({
        state: 'healthy',
        updatedAt: new Date().toISOString(),
        message: 'Local mirror; external status checks disabled in defence variant.',
      });
    } else {
      setStatus({
        state: 'healthy',
        updatedAt: new Date().toISOString(),
        statusUrl: 'https://status.geosoft.com',
        message: 'All GeoViewer services are operating normally.',
      });
    }
  }, [defenceVariant.active]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    document.title = 'System status · GeoViewer';
    liveRegion.announce('status', 'System status loaded.');
  }, [liveRegion]);

  const handleRefresh = useCallback(() => {
    refresh();
    liveRegion.announce('status', 'Refreshed system status.');
  }, [refresh, liveRegion]);

  if (!status) {
    return (
      <main
        role="main"
        aria-labelledby="gvw-157-heading"
        data-gvw-id="GVW-157"
        data-route="/workspace/help/status"
        style={{
          minHeight: '100%',
          background: tokens.color.surface[0],
          color: tokens.color.ink.primary,
          fontFamily: tokens.font.family.ui,
          padding: tokens.space[5],
          boxSizing: 'border-box',
        }}
      >
        <h1
          id="gvw-157-heading"
          style={{ margin: 0, fontSize: tokens.font.size['2xl'], fontWeight: tokens.font.weight.semibold }}
        >
          System status
        </h1>
        <p style={{ color: tokens.color.ink.secondary }}>Loading…</p>
      </main>
    );
  }

  const glyph = STATUS_GLYPH[status.state];

  return (
    <main
      role="main"
      aria-labelledby="gvw-157-heading"
      data-gvw-id="GVW-157"
      data-route="/workspace/help/status"
      style={{
        minHeight: '100%',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <h1
        id="gvw-157-heading"
        style={{
          margin: 0,
          marginBottom: tokens.space[4],
          fontSize: tokens.font.size['2xl'],
          fontWeight: tokens.font.weight.semibold,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[3],
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 28 }}>
          monitor_heart
        </span>
        System status
      </h1>

      <div
        style={{
          padding: tokens.space[5],
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderLeft: `${tokens.space[1]}px solid ${glyph.color}`,
          borderRadius: tokens.radius.md,
          maxWidth: 720,
          marginBottom: tokens.space[5],
        }}
      >
        <div
          aria-label={`System status: ${glyph.label}`}
          style={{ display: 'flex', alignItems: 'center', gap: tokens.space[3], marginBottom: tokens.space[3] }}
        >
          <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 32, color: glyph.color }}>
            {glyph.icon}
          </span>
          <div>
            <div style={{ fontSize: tokens.font.size.xl, fontWeight: tokens.font.weight.semibold, color: glyph.color }}>
              {glyph.label}
            </div>
            <div style={{ fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary }}>
              Updated {new Date(status.updatedAt).toLocaleString()}
            </div>
          </div>
        </div>
        <p style={{ marginTop: 0, marginBottom: 0, color: tokens.color.ink.primary }}>{status.message}</p>
      </div>

      <div style={{ display: 'flex', gap: tokens.space[3], flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={handleRefresh}
          style={{
            padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
            background: tokens.color.surface[2],
            color: tokens.color.ink.primary,
            border: tokens.border.thin,
            borderRadius: tokens.radius.md,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            fontSize: tokens.font.size.md,
          }}
        >
          <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18 }}>
            refresh
          </span>
          Refresh status
        </button>

        {status.statusUrl && !defenceVariant.active ? (
          <a
            href={status.statusUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open status page in new tab"
            style={{
              padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
              background: tokens.color.accent.primary,
              color: tokens.color.accent.on,
              border: 'none',
              borderRadius: tokens.radius.md,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[2],
              fontSize: tokens.font.size.md,
              fontWeight: tokens.font.weight.medium,
            }}
          >
            <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18 }}>
              open_in_new
            </span>
            Open detailed status page
          </a>
        ) : null}
      </div>
    </main>
  );
}
